import { createContext, useContext, ReactNode, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useProgressData } from "@/hooks/useProgressData";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface UserStats {
  xp: number;
  level: number;
  streak: number;
}

interface UserBadge {
  badge_slug: string;
  earned_at: string | null;
}

interface Quest {
  id: string;
  slug: string;
  title: string;
  xp: number;
  min_level: number | null;
}

interface UserQuest {
  quest_id: string;
  completed_at: string | null;
}

interface ProgressContextType {
  stats: UserStats;
  badges: UserBadge[];
  quests: Quest[];
  userQuests: UserQuest[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const defaultStats: UserStats = { xp: 0, level: 1, streak: 0 };

const ProgressContext = createContext<ProgressContextType | undefined>(undefined);

export const ProgressProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const { stats, badges, quests, userQuests, loading, error, refetch } = useProgressData(user?.id);
  const hasAwardedDailyXP = useRef(false);

  // Daily XP awarding - once per session
  useEffect(() => {
    if (!user?.id || hasAwardedDailyXP.current) return;

    const checkAndAwardDailyXP = async () => {
      try {
        const { data: streakData } = await supabase
          .from('user_streaks')
          .select('last_action')
          .eq('user_id', user.id)
          .single();

        const lastAction = streakData?.last_action;
        const today = new Date().toISOString().split('T')[0];
        const lastActionDate = lastAction ? new Date(lastAction).toISOString().split('T')[0] : null;

        if (lastActionDate !== today) {
          // Update streak
          const { data: newStreak } = await supabase.rpc('update_user_streak', { target_user_id: user.id });
          
          // Award XP
          const baseXP = 10;
          const streakBonus = Math.min((newStreak || 1) - 1, 6) * 2;
          const totalXP = baseXP + streakBonus;
          
          await supabase.rpc('award_xp', { target_user_id: user.id, amount: totalXP });
          
          hasAwardedDailyXP.current = true;
          
          toast.success(`Daily Login: +${totalXP} XP`, {
            description: newStreak && newStreak > 1 ? `${newStreak} day streak!` : 'Welcome back!',
            duration: 4000
          });
          
          refetch();
        }
      } catch (err) {
        console.error('Error awarding daily XP:', err);
      }
    };

    checkAndAwardDailyXP();
  }, [user?.id, refetch]);

  return (
    <ProgressContext.Provider value={{ stats: stats || defaultStats, badges, quests, userQuests, loading, error, refetch }}>
      {children}
    </ProgressContext.Provider>
  );
};

export const useProgress = () => {
  const context = useContext(ProgressContext);
  if (context === undefined) {
    throw new Error("useProgress must be used within a ProgressProvider");
  }
  return context;
};
